import React, { useEffect, useState } from "react";
import { Row, Col, Spin } from "antd";
import { Chart } from 'react-charts';
import styled from "styled-components";
import DashboardProvider from "../../../../../../shared/provider/DashboardProvider";
import { MonthlyEvaluationDTO } from "../../../../../../shared/dto/MonthlyEvaluationDto";

const ChartContainer = styled.div`
  background-color: #f0f0f0;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
  max-height: 350px;
  height: 100%;
`;

const ChartTitle = styled.h3`
  margin-bottom: 8px;
`;

const TANI_META = 75; // Example meta value for TANI
const TEIPP_META = 45; // Example meta value for TEIPP
const TCIAPC_META = 25; // Example meta value for TCIAPC
const TEPI_META = 70; // Example meta value for TEPI

function Indicadores(): JSX.Element {
  const [monthlyEvaluationData, setMonthlyEvaluationData] = useState<MonthlyEvaluationDTO[]>([]);
  const [teippMonthlyEvaluationData, setTeippMonthlyEvaluationData] = useState<MonthlyEvaluationDTO[]>([]);
  const [tciapcMonthlyEvaluationData, setTciapcMonthlyEvaluationData] = useState<MonthlyEvaluationDTO[]>([]);
  const [tepiMonthlyEvaluationData, setTepiMonthlyEvaluationData] = useState<MonthlyEvaluationDTO[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMonthlyEvaluationData = async () => {
      const dashboardProvider = new DashboardProvider();
      try {
        const response = await dashboardProvider.getMonthlyEvaluationPercentage();
        setMonthlyEvaluationData(response);
      } catch (error) {
        console.error("Erro ao buscar dados de avaliação mensal:", error);
      }
    };

    const fetchTeippMonthlyEvaluationData = async () => {
      const dashboardProvider = new DashboardProvider();
      try {
        const response = await dashboardProvider.getTEIPPMonthlyEvaluationPercentage();
        setTeippMonthlyEvaluationData(response);
      } catch (error) {
        console.error("Erro ao buscar dados de avaliação mensal TEIPP:", error);
      }
    };

    const fetchTciapcMonthlyEvaluationData = async () => {
      const dashboardProvider = new DashboardProvider();
      try {
        const response = await dashboardProvider.getTCIAPCMonthlyEvaluationPercentage();
        setTciapcMonthlyEvaluationData(response);
      } catch (error) {
        console.error("Erro ao buscar dados de avaliação mensal TCIAPC:", error);
      }
    };

    const fetchTepiMonthlyEvaluationData = async () => {
      const dashboardProvider = new DashboardProvider();
      try {
        const response = await dashboardProvider.getIdeasMovedFromIdeation();
        setTepiMonthlyEvaluationData(response);
      } catch (error) {
        console.error("Erro ao buscar dados de avaliação mensal TEPI:", error);
      }
    };

    Promise.all([fetchMonthlyEvaluationData(), fetchTeippMonthlyEvaluationData(), fetchTciapcMonthlyEvaluationData(), fetchTepiMonthlyEvaluationData()]).finally(() => {
      setLoading(false);
    });
  }, []);

  const monthlyEvaluationConfig = React.useMemo(
    () => [
      {
        label: 'Avaliação Mensal',
        data: monthlyEvaluationData.map(item => ({
          primary: item.month,
          secondary: item.percentage,
        })),
      },
      {
        label: 'Meta',
        data: monthlyEvaluationData.map(item => ({
          primary: item.month,
          secondary: TANI_META,
        })),
      },
    ],
    [monthlyEvaluationData]
  );

  const teippMonthlyEvaluationConfig = React.useMemo(
    () => [
      {
        label: 'Avaliação Mensal TEIPP',
        data: teippMonthlyEvaluationData.map(item => ({
          primary: item.month,
          secondary: item.percentage,
        })),
      },
      {
        label: 'Meta',
        data: teippMonthlyEvaluationData.map(item => ({
          primary: item.month,
          secondary: TEIPP_META,
        })),
      },
    ],
    [teippMonthlyEvaluationData]
  );

  const tciapcMonthlyEvaluationConfig = React.useMemo(
    () => [
      {
        label: 'Avaliação Mensal TCIAPC',
        data: tciapcMonthlyEvaluationData.map(item => ({
          primary: item.month,
          secondary: item.percentage,
        })),
      },
      {
        label: 'Meta',
        data: tciapcMonthlyEvaluationData.map(item => ({
          primary: item.month,
          secondary: TCIAPC_META,
        })),
      },
    ],
    [tciapcMonthlyEvaluationData]
  );

  const tepiMonthlyEvaluationConfig = React.useMemo(
    () => [
      {
        label: 'Avaliação Mensal TEPI',
        data: tepiMonthlyEvaluationData.map(item => ({
          primary: item.month,
          secondary: item.percentage,
        })),
      },
      {
        label: 'Meta',
        data: tepiMonthlyEvaluationData.map(item => ({
          primary: item.month,
          secondary: TEPI_META,
        })),
      },
    ],
    [tepiMonthlyEvaluationData]
  );

  const primaryAxis = React.useMemo(
    () => ({
      getValue: (datum: any) => datum.primary,
      type: 'ordinal',
    }),
    []
  );

  const secondaryAxes = React.useMemo(
    () => [
      {
        getValue: (datum: any) => datum.secondary,
        type: 'linear',
        elementType: 'line',
        tickFormat: (tick: number) => `${tick}%`,
        tickCount: 5,
        showGrid: false,
      },
    ],
    []
  );

  if (loading) {
    return <Spin />;
  }

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} sm={24} md={12}>
        <ChartContainer>
          <ChartTitle>Taxa de Atribuição de Notas a Ideias (TANI)</ChartTitle>
          <div style={{ height: 200, width: '100%' }}>
            {monthlyEvaluationData.length > 0 && (
              <Chart 
                options={{
                  data: monthlyEvaluationConfig,
                  primaryAxis,
                  secondaryAxes,
                  resizable: true,
                  defaultColors: ['#1351b4', '#000']
                }}
              />
            )}
          </div>
        </ChartContainer>
      </Col>
      <Col xs={24} sm={24} md={12}>
        <ChartContainer>
          <ChartTitle>Taxa de Execução de Ideações do Período Previsto (TEIPP)</ChartTitle>
          <div style={{ height: 200, width: '100%' }}>
            {teippMonthlyEvaluationData.length > 0 && (
              <Chart 
                options={{
                  data: teippMonthlyEvaluationConfig,
                  primaryAxis,
                  secondaryAxes,
                  resizable: true,
                  defaultColors: ['#1351b4', '#000']
                }}
              />
            )}
          </div>
        </ChartContainer>
      </Col>
      <Col xs={24} sm={24} md={12}>
        <ChartContainer>
          <ChartTitle>Taxa de Conversão de Ideias Aprovadas em Propostas a Clientes (TCIAPC)</ChartTitle>
          <div style={{ height: 200, width: '100%' }}>
            {tciapcMonthlyEvaluationData.length > 0 && (
              <Chart 
                options={{
                  data: tciapcMonthlyEvaluationConfig,
                  primaryAxis,
                  secondaryAxes,
                  resizable: true,
                  defaultColors: ['#1351b4', '#000']
                }}
              />
            )}
          </div>
        </ChartContainer>
      </Col>
      <Col xs={24} sm={24} md={12}>
        <ChartContainer>
          <ChartTitle>Taxa de Eficiência do Processo de Inovação (TEPI)</ChartTitle>
          <div style={{ height: 200, width: '100%' }}>
            {tepiMonthlyEvaluationData.length > 0 && (
              <Chart 
                options={{
                  data: tepiMonthlyEvaluationConfig,
                  primaryAxis,
                  secondaryAxes,
                  resizable: true,
                  defaultColors: ['#1351b4', '#000']
                }}
              />
            )}
          </div>
        </ChartContainer>
      </Col>
    </Row>
  );
}

export default Indicadores;
